import { Policy, PolicySetting, PolicySettingsGroup } from '../../../types/types';
import { SQUASH_SETTINGS } from './editorTransformation/config';
import { LIMIT_TO_USERS_VALUE } from '../../../consts';

const LIMIT_TO_USERS_SETTING = 'limitToUsers';

/**
 * Get the "disabled" value for a dropdown setting
 * Finds the dropdown value that sets all underlying booleans to false
 */
const getDropdownDisabledValue = (dropdownId: string): string | null => {
    const squashConfig = SQUASH_SETTINGS[dropdownId];
    if (!squashConfig) {
        return null;
    }

    // Find the value where all booleans are false
    for (const [dropdownValue, mapping] of Object.entries(squashConfig.rules)) {
        const mappingsToCheck = Array.isArray(mapping) ? mapping : [mapping];
        
        // Check if all booleans are false in this mapping
        const allFalse = mappingsToCheck.every((booleanMapping) =>
            Object.values(booleanMapping).every((val) => val === false)
        );

        if (allFalse) {
            return dropdownValue;
        }
    }

    return null;
};

/**
 * Edit a policy setting, optionally disabling other conflicting settings atomically
 * 
 * @param settingId - ID of the setting to change
 * @param groupType - Group the setting belongs to
 * @param settingChanges - Changes to apply to the setting
 * @param policy - Current policy state
 * @param settingsToDisable - Optional array of setting IDs to disable (for conflict resolution)
 * @returns Updated policy
 */
const editPolicy = (
    settingId: string,
    groupType: PolicySettingsGroup,
    settingChanges: Partial<PolicySetting>,
    policy: Policy,
    settingsToDisable?: string[],
): Policy => {
    let newSettingsList: PolicySetting[] = [...policy.settings[groupType]];
    
    // Apply the main setting change
    const settingIndex = newSettingsList.findIndex(({ id }) => id === settingId);
    newSettingsList[settingIndex] = { ...newSettingsList[settingIndex], ...settingChanges };

    if (
        groupType === 'access'
        && settingId === LIMIT_TO_USERS_VALUE
        && Array.isArray(settingChanges.value)
        && settingChanges.value.length === 0
    ) {
        newSettingsList = newSettingsList.map((setting) => (
            setting.id === LIMIT_TO_USERS_SETTING
                ? { ...setting, value: false }
                : setting
        ));
    }
    
    // If there are settings to disable (due to conflict resolution), disable them
    if (settingsToDisable && settingsToDisable.length > 0) {
        newSettingsList = newSettingsList.map((setting) => {
            // Check for exact match or if it's a squashed dropdown setting (e.g., "dropdownId.value")
            const shouldDisable = settingsToDisable.some((idToDisable) => {
                if (idToDisable === setting.id) {
                    return true;
                }
                // Handle dropdown.value pattern (e.g., "forwardingOptions.withAnyone")
                if (idToDisable.includes('.') && idToDisable.startsWith(`${setting.id}.`)) {
                    return true;
                }
                return false;
            });

            if (shouldDisable) {
                // Handle boolean settings
                if (typeof setting.value === 'boolean') {
                    return { ...setting, value: false };
                }
                // Handle dropdown settings - set to the disabled value
                if (typeof setting.value === 'string') {
                    const disabledValue = getDropdownDisabledValue(setting.id);
                    if (disabledValue) {
                        return { ...setting, value: disabledValue };
                    }
                }
            }
            return setting;
        });
    }
    
    return {
        ...policy,
        settings: {
            ...policy.settings,
            [groupType]: newSettingsList,
        },
    };
};

export default editPolicy;
